import { css } from '@emotion/react'
import { s3URL } from '@mathflat/shared/@common/utils/s3'
import { format } from 'date-fns'
import { observer } from 'mobx-react'
import { useEffect, useState } from 'react'
import { generatePath, useNavigate } from 'react-router'

import { routeName } from '~/@common/constants'
import { useDailyWorksheetRemainingTime } from '~/@common/hooks/useDailyWorksheetRemainingTime'
import { useRepository } from '~/@common/hooks/useRepository'
import { toastService } from '~/@common/services'
import { commonRepo } from '~/@common/services/repo.service'
import { typo } from '~/@common/styles'
import Button from '~/@common/ui/(Button)/Button/Button'
import { mixpanelClient } from '~/@common/utils/trackings/mixpanel'

import DailyLearningService from '../../@service/DailyLearning.service'
import SectionHeader from '../SectionHeader'
import DailyLearningTooltip from './DailyLearningTooltip'

const getStatusText = (service: DailyLearningService) => {
  const { todayLearning, isConsecutiveDailyLearning, isMonday, consecutiveDailyLearningCount } =
    service

  switch (todayLearning?.status) {
    case 'NOT_STARTED':
      return (
        <>
          오늘의 학습을 아직 하지 않았어요. <br />
          학습을 시작하세요!
        </>
      )
    case 'IN_PROGRESS':
      return (
        <>
          학습이 진행중이예요.
          <br />
          오늘이 끝나기 전에 마무리해요!
        </>
      )
    case 'COMPLETED':
      if (isConsecutiveDailyLearning) {
        return (
          <>
            오늘의 학습을
            <br />
            <strong className="consecutive-daily-learning-count">
              {consecutiveDailyLearningCount}일 연속
            </strong>
            &nbsp;달성했어요!
          </>
        )
      }
      // 월요일은 연속된 학습이 항상 없으므로
      if (isMonday) {
        return (
          <>
            오늘의 학습을
            <br />
            달성했어요!
          </>
        )
      }
      return (
        <>
          연속 달성을 놓치지 않게
          <br />더 노력해봐요!
        </>
      )
    default:
      return null
  }
}

const DAILY_LEARNING_BUTTON_TEXT = {
  NOT_STARTED: '오늘의 학습 시작하기',
  IN_PROGRESS: '오늘의 학습 이어서 하기',
  COMPLETED: '학습 결과 보기',
}

const DAIILY_LEARNING_STATUS_IMAGE = {
  NOT_STARTED: s3URL.student('images/daily_learning_not_started.png'),
  IN_PROGRESS: s3URL.student('images/daily_learning_in_progress.png'),
  COMPLETED: s3URL.student('assets/mathflat/complete_fire.gif'),
}

const DAYS_OF_WEEK = {
  MONDAY: '월',
  TUESDAY: '화',
  WEDNESDAY: '수',
  THURSDAY: '목',
  FRIDAY: '금',
  SATURDAY: '토',
  SUNDAY: '일',
}

const DailyLearningSection = () => {
  const [isStartingDailyLearning, setIsStartingDailyLearning] = useState(false)
  const service = useRepository(DailyLearningService)
  const { isDailyWorksheetAllowed } = commonRepo.permission
  const dailyWorksheetRemainingTime = useDailyWorksheetRemainingTime(isDailyWorksheetAllowed)

  const navigate = useNavigate()

  useEffect(() => {
    if (!commonRepo.student?.id) return
    service.loadDailyLearning(commonRepo.student?.id)
  }, [commonRepo.student?.id])

  const handleStartDailyLearning = async () => {
    if (!commonRepo.student?.id) return

    if (service.todayLearning?.status === 'NOT_STARTED') {
      mixpanelClient.track('오늘의학습_오늘의학습시작하기버튼_Click', {
        최대연속학습수행일자: service.maxConsecutiveWeeklyLearningCount,
      })
    } else if (service.todayLearning?.status === 'IN_PROGRESS') {
      mixpanelClient.track('오늘의학습_이어서학습하기버튼_Click', {
        최대연속학습수행일자: service.maxConsecutiveWeeklyLearningCount,
      })
    } else if (service.todayLearning?.status === 'COMPLETED') {
      mixpanelClient.track('오늘의학습_학습결과보기버튼_Click', {
        최대연속학습수행일자: service.maxConsecutiveWeeklyLearningCount,
      })
    }

    if (service.todayLearning?.studentWorksheetId === null) {
      try {
        setIsStartingDailyLearning(true)
        const studentWorksheetId = await service.startDailyLearning(commonRepo.student.id)
        navigate(generatePath(routeName.student.studentWorksheetById, { studentWorksheetId }))
      } catch (error) {
        toastService.error('학습범위가 설정되지 않아 학습할 수 없어요. 선생님에게 문의하세요.')
      } finally {
        setIsStartingDailyLearning(false)
      }
    } else {
      navigate(
        generatePath(
          service.todayLearning?.status === 'COMPLETED'
            ? routeName.student.studentWorksheetScoringById
            : routeName.student.studentWorksheetById,
          {
            studentWorksheetId: service.todayLearning?.studentWorksheetId,
          },
        ),
      )
    }
  }

  if (!service.todayLearning) return null

  return (
    <section css={S}>
      <SectionHeader title="오늘의 학습">
        <DailyLearningTooltip />
      </SectionHeader>
      <div className="content">
        {service.todayLearning && (
          <>
            {service.todayLearning?.status === 'COMPLETED' ? (
              <div className="learning-remaining-time">학습완료</div>
            ) : (
              <div className="learning-remaining-time">
                오늘까지
                <div className="time">
                  <img src={s3URL.student('images/daily_learning_time.png')} alt="time" />
                  <p>{format(dailyWorksheetRemainingTime, 'HH:mm:ss')}</p>
                </div>
                남았어요
              </div>
            )}

            <div className="learning-status">{getStatusText(service)}</div>
            <div className="learning-weekly-info">
              {service.dailyLearning?.map((day) => (
                <div key={day.day} className="weekly-day-container">
                  <img src={DAIILY_LEARNING_STATUS_IMAGE[day.status]} alt="time" />
                  <span key={day.day} className="weekly-day">
                    {DAYS_OF_WEEK[day.day]}
                  </span>
                </div>
              ))}
            </div>
            <Button
              theme="primary"
              size="small"
              isLoading={isStartingDailyLearning}
              disabled={isStartingDailyLearning}
              onClick={handleStartDailyLearning}
              style={{
                height: '36px',
                backgroundColor: 'var(--Colors-Gray-900)',
                borderRadius: 'var(--Radius-300)',
              }}
            >
              {service.todayLearning?.status &&
                DAILY_LEARNING_BUTTON_TEXT[service.todayLearning?.status]}
            </Button>
          </>
        )}
      </div>
    </section>
  )
}

export default observer(DailyLearningSection)

const S = css`
  display: flex;
  flex-direction: column;

  .content {
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 24px;
    background-color: var(--White);
    border-radius: var(--Radius-300);
    height: 252px;

    .learning-remaining-time {
      display: flex;
      align-items: center;
      gap: 4px;
      ${typo.caption01};
      color: var(--Colors-Blue-600);
      font-weight: 700;
    }

    .time {
      display: flex;
      align-items: center;
      padding: 4px 12px;
      gap: 5px;
      border-radius: var(--Radius-Circle);
      background: var(--Colors-Indigo-50);

      img {
        width: 12px;
        height: 14px;
      }
    }

    .learning-status {
      ${typo.heading05};
      color: var(--Colors-Gray-900);
      font-weight: 700;
    }

    .learning-weekly-info {
      display: flex;
      justify-content: space-between;

      .weekly-day-container {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 2px;

        img {
          width: 38px;
          height: 38px;
          border-radius: var(--Radius-300);
        }

        span {
          ${typo.caption01};
          color: var(--Colors-Gray-600);
        }
      }
    }

    .consecutive-daily-learning-count {
      color: var(--Colors-Blue-600);
      font-weight: 700;
    }
  }
`
